import type { Mm, PlacedBrick } from '@bw/core';
import type { WallPlacement } from '@bw/document';
import type { Vec2, Vec3, WorldBrick } from './types.js';

/**
 * Wall local to world (design document, phase 1 §4).
 *
 * A wall's own frame is the elevation you draw: x along the wall, y up, and
 * +z through the thickness from the outer face at zero. It reaches the world
 * by a rotation about Y followed by a translation — no axis conversion and no
 * sign flips, which is the whole reason the 2D and 3D views can share a model.
 *
 *   Ry(t) = [  cos t  0  sin t ]
 *           [  0      1  0     ]
 *           [ -sin t  0  cos t ]
 *
 * Positive rotation is counter-clockwise viewed from above, where "above"
 * means looking down the -Y axis at a plan with +x to the right and +z down
 * the page. Check it against a quarter turn: (1, 0, 0) goes to (0, 0, -1),
 * which is right-hand side to top of page, and that is counter-clockwise.
 */
export function wallToWorld(placement: WallPlacement, x: Mm, y: Mm, z: Mm): Vec3 {
  const t = (placement.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  return {
    x: placement.origin.x + x * cos + z * sin,
    y: placement.origin.y + y,
    z: placement.origin.z - x * sin + z * cos,
  };
}

/** The same rotation, in plan only — the form the plan editor works in. */
export function wallToPlan(placement: WallPlacement, x: Mm, z: Mm): Vec2 {
  const t = (placement.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  return {
    x: placement.origin.x + x * cos + z * sin,
    z: placement.origin.z - x * sin + z * cos,
  };
}

/** World back to the wall's own plan frame — the inverse of `wallToPlan`. */
export function planToWall(placement: WallPlacement, x: Mm, z: Mm): Vec2 {
  const t = (placement.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  const dx = x - placement.origin.x;
  const dz = z - placement.origin.z;
  // Ry is orthonormal, so the inverse is the transpose.
  return { x: dx * cos - dz * sin, z: dx * sin + dz * cos };
}

/**
 * A brick's world placement. The brick is an axis-aligned box in the wall's
 * frame, so its centre transforms and its half-extents do not — they are
 * carried in the wall's frame alongside the wall's rotation, which is exactly
 * the position/scale/rotation triple an instanced renderer needs.
 */
export function brickToWorld(
  wallId: string,
  placement: WallPlacement,
  brick: PlacedBrick,
): WorldBrick {
  const centre = wallToWorld(
    placement,
    brick.x + brick.width / 2,
    brick.y + brick.height / 2,
    placement.thicknessMm / 2,
  );
  return {
    wallId,
    id: brick.id,
    materialId: brick.materialId,
    centre,
    half: {
      x: brick.width / 2,
      y: brick.height / 2,
      z: placement.thicknessMm / 2,
    },
    rotationDeg: placement.rotationDeg,
  };
}
